<template>
  <div v-if="entry">
    <BreadCrumb class="hidden sm:block" />
    <MarketingPage :entry="entry" :social-media-accounts="socialMediaAccounts" />
  </div>
</template>
<script>
import gql from 'graphql-tag';
import { stripLocale } from '@/utils/i18n';
import { SeomaticFragment, EntryPageFragment } from '@/mixins/entryPage';
import MarketingPage, { MarketingPageFragment } from '@/components/marketing/MarketingPage';
import { SocialMediaAccountsFragment } from '@/components/layout/footer/SocialMediaAccounts';
import { useEntryPage } from '@/composables/useEntryPage';
import BreadCrumb from '@/components/layout/BreadCrumb';
import { checkContentProtection } from '@/utils/contentProtection';
import { useBlackrollAuth } from '@/composables/useBlackrollAuth';
import { useLoyalty } from '@/composables/useLoyalty';
import { useUserData } from '@/composables/useUserData';

export const MarketingPageQuery = gql`
  query MarketingPageQuery($site: String!, $uri: String!) {
    entry(section: "marketing", site: [$site], uri: [$uri]) {
      typeHandle
      ...EntryPageFragment
      ...MarketingPageFragment
    }

    socialSet: globalSet(site: [$site], handle: "socialMediaAccounts") {
      ...SocialMediaAccountsFragment
    }

    seomatic(site: $site, uri: $uri, asArray: true) {
      ...SeomaticFragment
    }
  }

  ${EntryPageFragment}
  ${MarketingPageFragment}
  ${SeomaticFragment}
  ${SocialMediaAccountsFragment}
`;

export default defineNuxtComponent({
  name: 'MarketingPageController',

  components: { MarketingPage, BreadCrumb },

  async asyncData() {
    const { $craftRequest } = useNuxtApp();
    const { locale } = useNuxtApp().$i18n;
    const auth = useBlackrollAuth();
    const loyalty = useLoyalty();
    const userData = useUserData();
    const route = useRoute();

    const uri = stripLocale(route.path, locale.value);
    const variables = { site: locale.value, uri };

    const { entry, seomatic, socialMediaAccounts } = await $craftRequest(MarketingPageQuery, variables);

    // Check content protection before rendering
    await checkContentProtection(entry, useNuxtApp(), auth, loyalty, userData);

    return { entry, seomatic, socialMediaAccounts };
  },

  data() {
    return { socialMediaAccounts: [], entryPage: null };
  },

  created() {
    this.entryPage = useEntryPage(this.entry, this.seomatic);
  },
});
</script>
